import { parse } from 'csv-parse/browser/esm/sync';

/** One value per hour of a non-leap year — what the PV calculator expects for load and CHP profiles. */
export const HOURLY_PROFILE_LENGTH = 24 * 365;

/** Either the profile values or the translation key of the message to show the user. */
export type HourlyProfileCsvResult = { values: number[] } | { error: string };

/**
 * Reads an hourly profile from the first column of a CSV that starts with a header row.
 *
 * Never throws: csv-parse rejects files whose rows have more columns than the header (e.g. a
 * semicolon export with decimal commas), and an upload handler that let that escape gave the user
 * no feedback at all (ADMI-FRONTEND-6E).
 */
export function parseHourlyProfileCsv(text: string): HourlyProfileCsvResult {
    let rows: string[][];
    try {
        rows = parse(text, { from: 2 });
    } catch {
        return { error: 'Could not parse CSV file. Please check the contents.' };
    }

    // Number, not parseFloat: parseFloat reads a timestamp column ('2025-01-01 00:00') as 2025 and a
    // semicolon row csv-parse kept as one field ('01.01.2025 00:00;1.2') as 1.01.
    const values = rows.map((row) => {
        const raw = row[0]?.trim();
        return raw ? Number(raw) : NaN;
    });
    if (values.length !== HOURLY_PROFILE_LENGTH || !values.every(Number.isFinite)) {
        return { error: 'CSV file does not match the expected format. Please check the contents.' };
    }
    return { values };
}
